import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/opt/build/repo/www/src/components/mdx/index.tsx";
import { graphql } from 'gatsby';
import { ComponentHeader, ComponentFooter } from 'components/thumbprint-components';
export const pageQuery = graphql`
    {
        # Get links to by path to display in the navbar.
        platformNav: allSitePage(filter: { path: { glob: "/components/radio/*/" } }) {
            edges {
                node {
                    ...PlatformNavFragment
                }
            }
        }
    }
`;
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const layoutProps = {
  pageQuery,
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <ComponentHeader data={props.data} mdxType="ComponentHeader" />
    <h2>{`Summary`}</h2>
    <p>{`Control for selecting an option inside a form.`}</p>
    <p><img parentName="p" {...{
        "src": "/332cb9ac1f3d27aad5eb9c27a6bbc96e/default.png",
        "alt": "default"
      }}></img></p>
    <h2>{`Accessibility`}</h2>
    <p>{`Set `}<inlineCode parentName="p">{`adjustsFontForContentSizeCategory: true`}</inlineCode>{` (the default) to enable Dynamic Type support for a Radio.`}</p>
    <h2>{`Public API`}</h2>
    <h3>{`Radio`}</h3>
    <h4><inlineCode parentName="h4">{`class Radio: Control, SimpleControl`}</inlineCode></h4>
    <p>{`Represents only the round button part of the control. Except for very custom UI shouldn’t see direct use.`}</p>
    <p>{`Some of its basic control API is in common with LabeledRadio.`}</p>
    <h3>{`LabeledRadio`}</h3>
    <h4><inlineCode parentName="h4">{`class LabeledRadio, Control, SimpleControl`}</inlineCode></h4>
    <p>{`The full radio control with a label. Can be customized with a more complex content view but needs to always have a label set as its main, which will reflect its disabled state etc.`}</p>
    <h4>{`Full Initializer`}</h4>
    <pre><code parentName="pre" {...{}}>{`public init(
    control: Radio = Radio(),
    label: UILabel,
    content: (UIView & UIContentSizeCategoryAdjusting)? = nil,
    adjustsFontForContentSizeCategory: Bool = true
)
`}</code></pre>
    <p>{`Custom initializer, use when you need something more complex than a standard radio control with a simple label next to it.`}</p>
    <ul>
      <li parentName="ul">{`control: Pass in a custom `}<inlineCode parentName="li">{`Radio`}</inlineCode>{` if needed, should be very rarely used.`}</li>
      <li parentName="ul">{`label: The reference label. The radio will be vertically aligned against the center of label’s first line. It will also reflect the radio state.`}</li>
      <li parentName="ul">{`content: Optionally, a view to use as the radio’s content. If not nil, `}<inlineCode parentName="li">{`label`}</inlineCode>{` must be a part of it.`}</li>
      <li parentName="ul">{`adjustsFontForContentSizeCategory: Pass in `}<inlineCode parentName="li">{`false`}</inlineCode>{` if the UI where it will be displayed doesn’t support dynamic font sizes.`}</li>
    </ul>
    <h4><inlineCode parentName="h4">{`public convenience init(text: String? = nil, adjustsFontForContentSizeCategory: Bool = true)`}</inlineCode></h4>
    <p>{`Convenience initializer for the standard radio button theme. The label will be set to a simple `}<inlineCode parentName="p">{`Label`}</inlineCode>{` with `}<inlineCode parentName="p">{`.text1`}</inlineCode>{` theme.`}</p>
    <h4><inlineCode parentName="h4">{`public var text: String?`}</inlineCode></h4>
    <p>{`Convenience access to the label’s text`}</p>
    <h4><inlineCode parentName="h4">{`public var attributedText: NSAttributedString?`}</inlineCode></h4>
    <p>{`Allows for setting formatted text of the labeled radio’s label. Keep in mind that Thumbprint theming will mostly be ignored if this is set.`}</p>
    <h4><inlineCode parentName="h4">{`public enum LabeledControl.ContentPlacement: CaseIterable { case leading case trailing }`}</inlineCode></h4>
    <p>{`Available values for the `}<inlineCode parentName="p">{`contentPlacement`}</inlineCode>{` property.`}</p>
    <h4><inlineCode parentName="h4">{`public var contentPlacement`}</inlineCode></h4>
    <p>{`Determines on which side of the labeled radio will the label and other content layout.`}</p>
    <p><img parentName="p" {...{
        "src": "/59de36b3d29d567ac8a1b94671242b01/contentPlacementLeadingLTR.png",
        "alt": "leading"
      }}></img></p>
    <p><img parentName="p" {...{
        "src": "/964a5a64c722a242038a11ab05d6f364/contentPlacementTrailingLTR.png",
        "alt": "trailing"
      }}></img></p>
    <h4><inlineCode parentName="h4">{`public var numberOfLines: Int`}</inlineCode></h4>
    <p>{`Convenience access to the label’s property of the same name. For behavior, look the documentation for `}<inlineCode parentName="p">{`UILabel.numberOfLines`}</inlineCode></p>
    <h4><inlineCode parentName="h4">{`public var contentInsets: NSDirectionalEdgeInsets`}</inlineCode></h4>
    <p>{`Allows for larger margins around the labeled radio contents. The additional area will also register touches.`}</p>
    <h4><inlineCode parentName="h4">{`public let label: UILabel`}</inlineCode></h4>
    <p>{`Direct read-only access to the labeled radio’s label. Usually it will be a `}<inlineCode parentName="p">{`Thumbprint.Label`}</inlineCode>{` control but if initialized with custom content it may be a `}<inlineCode parentName="p">{`UILabel`}</inlineCode>{` or a different subclass of it.`}</p>
    <h4><inlineCode parentName="h4">{`public func labelLayoutDidChange()`}</inlineCode></h4>
    <p>{`If the labeled radio’s formatting is modified you should always call this method, it will ensure that the radio button is correctly aligned against it.`}</p>
    <h4><inlineCode parentName="h4">{`public var isSelected: Bool`}</inlineCode></h4>
    <p>{`Manages selected state`}</p>
    <p><img parentName="p" {...{
        "src": "/487053134e75ef29f809593871425ba6/deselected.png",
        "alt": "unselected"
      }}></img></p>
    <h4><inlineCode parentName="h4">{`public var isEnabled: Bool`}</inlineCode></h4>
    <p>{`Manages enabled state`}</p>
    <p><img parentName="p" {...{
        "src": "/570ccb5ce7f3ccf562463065b7cd3b74/disabled.png",
        "alt": "disabled"
      }}></img></p>
    <h2>{`Usage`}</h2>
    <p>{`The `}<inlineCode parentName="p">{`Radio`}</inlineCode>{` class manages only the graphical radio button itself. The full control with a label (or several) that should be generally used is `}<inlineCode parentName="p">{`LabeledRadio`}</inlineCode>{`. The public API described above works for both classes other than `}<inlineCode parentName="p">{`contentPosition`}</inlineCode>{` which is specific to LabeledControl.`}</p>
    <p>{`Radio buttons should generally be used within a `}<inlineCode parentName="p">{`RadioGroup`}</inlineCode>{` to control exclusive selection. Components such as `}<inlineCode parentName="p">{`RadioStack`}</inlineCode>{` already set up the radio group for you and should be used wherever possible. To receive updates when the user selects a different radio button, subscribe to `}<inlineCode parentName="p">{`RadioGroup.selection`}</inlineCode>{`.`}</p>
    <h3>{`Example`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-swift"
      }}>{`import Combine

/* ... */

let titles = ["Apple", "Banana", "Orange"]
let radioStack = RadioStack(titles: titles, adjustsFontForContentSizeCategory: adjustsFontForContentSizeCategory)
radioStack.delegate = self
view.addSubview(radioStack)

/* ... */

radioStack.radioGroup.selection
    .receive(on: RunLoop.main)
    .sink { [weak self] selection in
        self?.radioStackDidSelectRadioAt(index: selection)
    }
    .store(in: &subscriptions)

`}</code></pre>


    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      